import { FC, SyntheticEvent, TouchEvent, useEffect, useState } from "react";

import classes from "./Image.module.css";
import SectionHeader from "../Layout/SectionHeader";
import BottomSticky from "../UI/BottomSticky";
import Button from "../UI/Button";
import CountUp from "react-countup";
import { Badge, Tab, Tabs } from "@mui/material";
import TabPanel from "@mui/material/Tabs";
import PendingBattle from "../battles/PendingBattle";
import OngoingBattle from "../battles/OngoingBattle";
import FinishedBattle from "../battles/FinishedBattle";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { userState } from "../../store/user";
import { setArtwork } from "../../store/artwork";
import GlobalAppLoading from "../UI/GlobalAppLoading";
import { TopicModelDocument } from "../votes/Vote";
import Markdown from "react-markdown";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getLang, i18n } from "../../i18n";

export interface BattleModelDocument {
  _id: string;
  user1ID: string;
  artwork1ID: string;
  artwork1Src: string;
  category: string;
  tags: string[];
  nextLevelBattleForUser1: boolean;
  nextLevelBattleForUser2: boolean;
  user2ID?: string;
  artwork2ID?: string;
  artwork2Src?: string;
  level: number;
  votesCount: number;
  artwork1VotesCount: number;
  artwork2VotesCount: number;
  winnerArtworkID?: string;
  status: string;
  winnerDecision: string;
  createdAt: Date;
  topic: TopicModelDocument;
}

export interface ArtworkSnapshotDocument {
  date: Date;
  profit: number;
  winrate: number;
  level: number;
  value: number;
}

interface ArtworkModelDocument {
  serverTime: number;
  _id: string;
  userID: string;
  slot: string;
  imageAmazonS3URL: string;
  title: string;
  profit: number;
  silentVotesCount: number;
  silentVotesWinner: number;
  battleVotesCount: number;
  battleVotesWinner: number;
  level: number;
  value: number;
  description: string;
  aiMentorReview: number;
  aiMentorDescription: string;
  category: string;
  tags: string[];
  pendingBattles: BattleModelDocument[];
  ongoingBattles: BattleModelDocument[];
  finishedBattles: BattleModelDocument[];
  wonBattles: number;
  snapshots: ArtworkSnapshotDocument[];
}

interface Props {
  setPopupImage: React.Dispatch<React.SetStateAction<string>>;
}

const Image: FC<Props> = ({ setPopupImage }) => {
  const lang = getLang();
  const t = i18n.image[lang];

  const API_URL = process.env.REACT_APP_API_URL;
  const user = useSelector(userState);
  const slots = ["free", "paid", "votes", "battles", "nfts", "invites"];
  const userSlots = [
    true,
    user.paidSlotUnlocked,
    user.votesSlotUnlocked,
    user.battlesSlotUnlocked,
    user.nftsSlotUnlocked,
    user.invitesSlotUnlocked,
  ];
  const navigate = useNavigate();
  const [art, setArt] = useState<ArtworkModelDocument | null>(null);
  const [screenIsLoading, setScreenIsLoading] = useState(true);
  const [value, setValue] = useState("pending");
  const [aiDescriptionExpanded, setAiDescriptionExpanded] = useState(false);
  const [slot, setSlot] = useState("");
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const handleTabChange = (
    e: SyntheticEvent<Element, Event>,
    tabIndex: any
  ) => {
    // window.scrollTo({
    //   top: 31,
    //   behavior: "smooth",
    // });
    setValue(tabIndex);
  };
  const level = art ? art.level : 0;
  let stars = new Array(level);
  for (let i = 0; i < level; i++) {
    stars.push(true);
  }
  for (let i = level; i < 7; i++) {
    stars.push(false);
  }
  const preUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setIsFileLoading(true);
      const formData = new FormData();
      formData.append("artwork", e.target.files[0]);
      axios
        .post(`${API_URL}/artworks`, formData, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${user.sessionToken}`,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          dispatch(setArtwork({ slot: slot, ...response.data.artwork }));
          setIsFileLoading(false);
          navigate("/upload");
        })
        .catch((err) => {
          console.log(err);
          setIsFileLoading(false);
        });
    }
  };
  const getArtworkBattles = async () => {
    setScreenIsLoading(true);
    await axios
      .get(`${API_URL}/artworks/slot/${slot}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${user.sessionToken}` },
      })
      .then((response) => {
        setArt(response.data.artwork);
        setScreenIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const data = art ? art.snapshots : [];

  const now = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(now.getMonth() - 1);

  let isBad = false;
  let isPerforming = false;

  // Filter snapshots from the last month
  const lastMonthSnapshots = data.filter(
    (s) => new Date(s.date) >= oneMonthAgo
  );

  // Sort by date ASC
  const sortedSnapshots = lastMonthSnapshots
    .slice()
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  console.log("sortedSnapshots", sortedSnapshots.length);

  // 1️⃣ isBad: last month average winrate <= 50
  if (sortedSnapshots.length >= 3) {
    const avgWinrateLastMonth =
      lastMonthSnapshots.reduce((sum, s) => sum + s.winrate, 0) /
      (lastMonthSnapshots.length || 1);

    console.log("avgWinrateLastMonth", avgWinrateLastMonth);

    isBad = avgWinrateLastMonth <= 0.5;

    // 2️⃣ isPerforming: last month snapshots sorted by date,
    const lastThree = sortedSnapshots.slice(-3);
    isPerforming =
      lastThree[0].winrate <= lastThree[1].winrate &&
      lastThree[1].winrate <= lastThree[2].winrate &&
      lastThree[2].winrate >= 0.5;
  }

  const slotIndex = slots.indexOf(slot);
  let prevSlot = slotIndex === 0 ? 5 : slotIndex - 1;
  let tryNo = 0;
  if (userSlots[prevSlot] !== true) {
    while (userSlots[prevSlot] !== true && tryNo <= 5) {
      prevSlot = prevSlot === 0 ? 5 : prevSlot - 1;
      tryNo++;
    }
  }
  tryNo = 0;
  let nextSlot = slotIndex === 5 ? 0 : slotIndex + 1;
  if (userSlots[nextSlot] !== true) {
    while (!userSlots[nextSlot] && tryNo <= 5) {
      nextSlot = nextSlot === 0 ? 5 : nextSlot + 1;
      tryNo++;
    }
  }

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 75;

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent<HTMLDivElement>) =>
    setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe || isRightSwipe)
      if (isRightSwipe) {
        //console.log("swipe", isLeftSwipe ? "left" : "right");
        // add your conditional logic here
        setSlot(slots[prevSlot]);
        navigate(`/image?slot=${slots[prevSlot]}`);
      }
    if (isLeftSwipe) {
      setSlot(slots[nextSlot]);
      navigate(`/image?slot=${slots[nextSlot]}`);
    }
  };
  useEffect(() => {
    console.log("artwork use effect");
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const querySlot = params.get("slot");
    if (querySlot) {
      setSlot(querySlot);
    }

    const getArtworkBySlot = async (slot: string) => {
      setScreenIsLoading(true);
      await axios
        .get(`${API_URL}/artworks/slot/${slot}`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        })
        .then((response) => {
          setArt(response.data.artwork);
          setScreenIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
        });
    };

    if (querySlot) getArtworkBySlot(querySlot);
  }, [slot]);
  if (isFileLoading) return <GlobalAppLoading text={t.loading} />;
  return (
    <>
      <SectionHeader
        label={art ? art.title : ``}
        back="/gallery"
        replace={
          !screenIsLoading && art && art.ongoingBattles.length === 0
            ? "/upload"
            : undefined
        }
        preUploadImage={preUploadImage}
      />
      <section className={classes.image}>
        <div className="container">
          {!screenIsLoading && art && (
            <div
              onTouchStart={(e) => {
                onTouchStart(e);
              }}
              onTouchMove={(e) => {
                onTouchMove(e);
              }}
              onTouchEnd={onTouchEnd}
            >
              {/* <h2>{art.title}</h2> */}
              {isBad && (
                <div className={classes.invite}>
                  <div className={classes.code}>{t.lowPerformance}</div>
                </div>
              )}
              {isPerforming && (
                <div className={classes.invite}>
                  <div className={classes.code}>{t.highPerformance}</div>
                </div>
              )}
              <div className={classes.preview}>
                <img
                  src={art.imageAmazonS3URL}
                  alt={art.title}
                  title={art.title}
                />
              </div>

              <div className={classes.half}>
                <div className={classes.quarterItem}>
                  <h2>{t.value}</h2>
                  {art.value > 0 && (
                    <CountUp
                      className={`${classes.count}`}
                      duration={1}
                      start={0.0}
                      end={Math.round(art.value)}
                      separator=","
                      suffix=" 💎"
                      decimals={0}
                      decimal="."
                    />
                  )}
                  {art.value === 0 && <div className={classes.count}>0 💎</div>}
                  {art.value < 0 && (
                    <div className={classes.count}>
                      {Math.round(art.value)} 💎
                    </div>
                  )}
                </div>
                <div className={classes.quarterItem}>
                  <h2>{t.profit}</h2>
                  {art.profit > 0 && (
                    <CountUp
                      className={`${classes.count}`}
                      duration={1}
                      start={0.0}
                      end={art.profit}
                      separator=","
                      suffix=" 🌕"
                      decimals={0}
                      decimal="."
                    />
                  )}
                  {art.profit === 0 && (
                    <div className={classes.count}>0 🌕</div>
                  )}
                  {art.profit < 0 && (
                    <div className={classes.count}>{art.profit} 🌕</div>
                  )}
                </div>
                {(art.silentVotesCount > 0 || art.battleVotesCount > 0) && (
                  <div className={classes.halfItem}>
                    <div className={classes.rate}>
                      <h2>{t.winRate}</h2>
                      <div className={classes.points}>
                        {art.silentVotesWinner + art.battleVotesWinner}/
                        {art.silentVotesCount + art.battleVotesCount}
                      </div>
                    </div>
                    <div className={classes.bar}>
                      <div
                        className={classes.barFilled}
                        style={{
                          width: `${
                            ((art.silentVotesWinner + art.battleVotesWinner) /
                              (art.silentVotesCount + art.battleVotesCount)) *
                            100
                          }%`,
                        }}
                      >
                        {(
                          ((art.silentVotesWinner + art.battleVotesWinner) /
                            (art.silentVotesCount + art.battleVotesCount)) *
                          100
                        ).toFixed(0)}
                        %
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <h2>{t.imageLevel}</h2>
              <div className={classes.level}>
                <div className={classes.stars}>
                  {stars.map((star) => (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 27 27"
                      fill="none"
                    >
                      <path
                        d="M8.53379 17.014L7.67934 21.9738C7.67934 22.4869 7.67934 23 8.53379 23C8.83874 23 12.6351 20.7766 12.9769 20.6056C13.7972 20.1951 14.6858 20.4346 15.0276 20.6056C15.3694 20.7766 19.1657 23 19.4707 23C20.3251 23 20.3251 22.4869 20.3251 21.9738L19.4707 17.014C19.4707 16.8429 19.2998 16.3299 19.8125 15.8168L23.572 12.0541C23.9708 11.598 24.4265 10.8569 23.2302 10.6859L17.7572 9.83074C17.0737 9.65971 16.7319 8.97559 16.561 8.63354L14.6812 4.52883C14.1344 3.57107 13.542 4.12976 13.3141 4.52883L11.2635 8.63354C11.0926 8.97559 10.9217 9.65971 10.2381 9.83074L4.76975 10.6859C3.57353 10.8569 4.02923 11.598 4.42798 12.0541L8.18754 15.8168C8.7002 16.3299 8.53379 16.8429 8.53379 17.014Z"
                        fill={star ? "#3095dd" : "#d2e9fb"}
                      />
                    </svg>
                  ))}
                  <svg
                    className={classes.nft}
                    xmlns="http://www.w3.org/2000/svg"
                    shapeRendering="geometricPrecision"
                    textRendering="geometricPrecision"
                    imageRendering="optimizeQuality"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    viewBox="0 0 512 474.47"
                  >
                    <path
                      d="m261.96 88.8 130.36 69.1c3.61 2.09 5.89 6.02 5.89 10.2v138.28c.02 2.29-.65 4.45-1.81 6.28-1.13 1.81-2.77 3.3-4.72 4.28l-129.81 68.79a11.695 11.695 0 0 1-5.96 1.55c-2.09-.02-4.12-.58-5.86-1.6l-130.36-69.1c-1.85-1.07-3.3-2.57-4.3-4.3l-.2-.39c-.91-1.71-1.39-3.61-1.39-5.51V168.1a11.6 11.6 0 0 1 1.8-6.28 11.96 11.96 0 0 1 4.72-4.28l129.82-68.8c1.86-1.06 3.92-1.56 5.96-1.55 2.09.03 4.12.58 5.86 1.61zm119.65 297.24a18.72 18.72 0 0 0-14.31.01c-2.22.92-4.23 2.26-5.91 3.93l-.12.12a18.45 18.45 0 0 0-3.65 5.19h-29.75v-24.8l-16.7 8.85v24.26c0 2.3.94 4.38 2.45 5.89a8.292 8.292 0 0 0 5.88 2.44h38.38a18.71 18.71 0 0 0 9.44 8.66c4.52 1.87 9.62 1.88 14.15.05l.14-.05c6.99-2.9 11.55-9.72 11.55-17.28 0-7.56-4.57-14.37-11.55-17.27zm41.96-25.96c-1.89-4.56-1.88-9.74.01-14.31 1.77-4.25 5.09-7.7 9.24-9.68v-29.74h-17.09v-16.7h25.4c2.3 0 4.38.93 5.89 2.44a8.298 8.298 0 0 1 2.44 5.89v38.38c3.9 2.01 6.98 5.39 8.66 9.44 1.87 4.52 1.88 9.62.05 14.15l-.05.14c-2.9 6.98-9.71 11.55-17.27 11.55-7.57 0-14.38-4.58-17.28-11.56zm52.97-95.43h-60.81l-.01-16.66h60.82c2-4.03 5.4-7.21 9.57-8.94 4.52-1.86 9.63-1.89 14.16-.05l.14.05a18.692 18.692 0 0 1 11.55 17.27c0 7.56-4.57 14.39-11.56 17.27a18.66 18.66 0 0 1-14.14.05l-.14-.05a18.655 18.655 0 0 1-9.58-8.94zm15.83-45.6h-76.65v-16.66h68.34v-54.43c-3.63-2.09-6.41-5.22-8.02-9.1a18.716 18.716 0 0 1 0-14.29c2.91-6.99 9.7-11.55 17.27-11.55 7.56 0 14.39 4.57 17.27 11.56 1.87 4.51 1.89 9.62.06 14.14l-.06.15c-1.87 4.5-5.41 8.09-9.88 10.01v61.84c0 2.3-.94 4.37-2.44 5.89a8.358 8.358 0 0 1-5.89 2.44zM427 178.81h-11.29v-11.63h.09c0-1.69-.17-3.38-.49-5.05h3.36v-24.61c-4.1-2-7.35-5.45-9.1-9.66a18.808 18.808 0 0 1 0-14.29c2.89-6.96 9.68-11.55 17.27-11.55 7.54 0 14.3 4.54 17.21 11.43l.07.14c1.89 4.58 1.88 9.7 0 14.29a18.8 18.8 0 0 1-8.79 9.52v33.08c0 2.3-.94 4.38-2.45 5.89a8.274 8.274 0 0 1-5.88 2.44zM136.07 88.42c4.25-1.77 7.7-5.09 9.68-9.25h29.74v29.4l16.7-8.85V70.87c0-2.3-.93-4.38-2.44-5.89a8.298 8.298 0 0 0-5.89-2.44h-38.37a18.685 18.685 0 0 0-23.6-8.71l-.14.05c-6.98 2.9-11.55 9.71-11.55 17.27 0 2.51.51 4.93 1.43 7.14 3.93 9.48 14.83 14.11 24.44 10.13zm81.12-52.96v51.01l16.66-8.83V35.46c4.03-1.99 7.22-5.42 8.94-9.58 1.87-4.52 1.89-9.62.05-14.15l-.05-.15A18.698 18.698 0 0 0 225.52.04c-7.56 0-14.38 4.57-17.28 11.55a18.779 18.779 0 0 0-.05 14.15l.06.14c1.71 4.13 4.92 7.6 8.94 9.58zm45.6-15.83c0 8.76-1.84 30.49-2.29 50.3 3.32.54 6.48 1.65 9.31 3.29l9.64 5.12v-50.4h54.43c.27.48.57.94.88 1.39 2.07 2.96 4.87 5.24 8.23 6.63 4.55 1.88 9.73 1.89 14.28 0 4.6-1.92 8.22-5.52 10.13-10.13.91-2.21 1.42-4.62 1.42-7.14a18.7 18.7 0 0 0-11.56-17.28 18.706 18.706 0 0 0-14.14-.05l-.14.06a18.57 18.57 0 0 0-6.07 4.05c-1.06 1.06-2 2.25-2.78 3.55-.44.73-.83 1.49-1.17 2.28h-61.84c-2.3 0-4.38.93-5.89 2.44a8.34 8.34 0 0 0-2.44 5.89zM303.03 85v5.83l16.68 8.84v-6.34h24.61c2 4.1 5.45 7.35 9.67 9.09 4.55 1.89 9.72 1.89 14.27 0a18.68 18.68 0 0 0 11.56-17.27c0-7.53-4.54-14.29-11.43-17.2l-.14-.07a18.808 18.808 0 0 0-14.29 0 18.882 18.882 0 0 0-9.52 8.79h-33.08c-2.3 0-4.38.94-5.89 2.44a8.298 8.298 0 0 0-2.44 5.89zm-214.6 28.57c1.89 4.57 1.88 9.75-.01 14.31-1.77 4.26-5.09 7.71-9.24 9.68v29.74h17.1v16.71H70.87c-2.3 0-4.38-.94-5.89-2.45a8.292 8.292 0 0 1-2.44-5.88V137.3c-3.9-2.01-6.98-5.39-8.66-9.44a18.685 18.685 0 0 1-.05-14.15l.05-.15a18.709 18.709 0 0 1 17.27-11.54c7.57 0 14.38 4.57 17.28 11.55zm-52.97 95.44h60.82v16.65H35.46a18.671 18.671 0 0 1-9.58 8.95 18.732 18.732 0 0 1-14.15.05l-.15-.05A18.724 18.724 0 0 1 .04 217.34c0-7.56 4.57-14.4 11.55-17.28 4.52-1.87 9.62-1.88 14.15-.05l.14.06c4.16 1.72 7.59 4.92 9.58 8.94zm-15.83 45.6h76.66v16.66H27.94v54.43c3.61 2.06 6.43 5.25 8.02 9.1 1.89 4.56 1.89 9.73 0 14.29-2.91 6.98-9.69 11.55-17.27 11.55a18.706 18.706 0 0 1-17.33-25.7l.06-.15c1.88-4.53 5.4-8.08 9.88-10.02v-61.83c0-2.3.94-4.38 2.44-5.89a8.316 8.316 0 0 1 5.89-2.44zM85 294.84h11.29v12.46h-.08c0 1.41.11 2.82.34 4.22h-3.22v24.61c4.11 2.02 7.34 5.43 9.1 9.67 1.88 4.57 1.88 9.71 0 14.28-2.89 6.96-9.68 11.56-17.27 11.56-7.54 0-14.3-4.55-17.21-11.44l-.06-.13a18.716 18.716 0 0 1 8.78-23.81v-33.09c0-2.29.94-4.37 2.44-5.88a8.324 8.324 0 0 1 5.89-2.45zM286.17 439v-46.41l-16.45 8.72-.2.11V439a18.75 18.75 0 0 0-8.95 9.58c-1.86 4.53-1.89 9.63-.05 14.16l.05.14c2.9 6.99 9.71 11.55 17.28 11.55 7.56 0 14.38-4.57 17.27-11.56 1.87-4.52 1.88-9.61.05-14.14l-.05-.14a18.77 18.77 0 0 0-8.95-9.59zm-45.6 15.84v-54.45l-16.66-8.83v54.97h-54.43a18.746 18.746 0 0 0-9.1-8.03 18.762 18.762 0 0 0-14.29.01c-6.98 2.91-11.54 9.69-11.54 17.27 0 7.55 4.57 14.39 11.55 17.27 4.51 1.87 9.61 1.89 14.15.05l.14-.05c4.52-1.87 8.08-5.41 10.02-9.88h61.83c2.3 0 4.38-.94 5.89-2.45a8.274 8.274 0 0 0 2.44-5.88zm-40.23-65.37v-10.4l-16.68-8.84v10.91h-24.61c-1.99-4.1-5.46-7.36-9.67-9.1a18.783 18.783 0 0 0-14.28 0 18.69 18.69 0 0 0-11.56 17.27c0 7.54 4.55 14.3 11.44 17.21l.13.06c4.57 1.89 9.72 1.89 14.29 0 2.22-.92 4.23-2.26 5.92-3.91l.16-.14c.88-.89 1.7-1.89 2.43-3 .37-.56.71-1.14 1.02-1.73h33.08c2.29 0 4.38-.94 5.88-2.45a8.283 8.283 0 0 0 2.45-5.88zm-38.77-114.26v-75.95h23.94l14.86 27.97h2.78v-27.97h24.35v75.95h-24.04l-14.65-29.96h-2.88v29.96h-24.36zm76.93 0v-75.95h50.71v18.54h-26.36v16.75h20.06v17.74h-20.06v22.92H238.5zm59.44-56.11v-19.84h52.49v19.84h-13.65v56.11h-24.35V219.1h-14.49zM261.8 107.58c37.1 20.24 75.27 39.62 112.72 59.33 4.22 2.22 6.63 6.53 6.64 11l.02 118.66c0 5.1-3.05 9.48-7.44 11.41L261.8 366.9a12.435 12.435 0 0 1-11.59 0c-37.1-20.24-75.27-39.62-112.72-59.33-4.23-2.22-6.64-6.54-6.64-11l-.03-118.66c0-5.1 3.06-9.48 7.45-11.41l111.94-58.92c3.77-1.98 8.08-1.84 11.59 0z"
                      fill="#d2e9fb"
                    />
                  </svg>
                </div>
              </div>
              <h2>{t.winRate}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data}>
                  <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) =>
                      new Date(date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                    minTickGap={10} // prevents overlap
                  />
                  <YAxis domain={[0, 1]} />
                  <Tooltip
                    labelFormatter={(date) =>
                      new Date(date).toLocaleDateString()
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="winrate"
                    stroke="#648bf4"
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
              <h2>{t.value}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data}>
                  <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) =>
                      new Date(date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                    minTickGap={10} // prevents overlap
                  />
                  <YAxis
                    tickFormatter={(v) => v} // optional, default is fine
                    interval={0} // show all ticks
                    domain={["dataMin", "dataMax"]}
                    tickCount={5} // roughly 5 ticks, will pick multiples automatically
                  />
                  <Tooltip
                    labelFormatter={(date) =>
                      new Date(date).toLocaleDateString()
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#d2597a"
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
              {art.description && (
                <>
                  <h2>{t.description}</h2>
                  <div className={classes.description}>{art.description}</div>
                </>
              )}
              {art.aiMentorReview && (
                <>
                  <div className={classes.rate}>
                    <h2>{t.aiMentorReview}</h2>
                    <div className={classes.points}>
                      {(art.aiMentorReview * 10).toFixed(1)} / 10
                    </div>
                  </div>
                  <div className={classes.bar}>
                    <div
                      className={classes.barFilled}
                      style={{ width: `${art.aiMentorReview * 100}%` }}
                    ></div>
                  </div>
                  {art.aiMentorDescription && (
                    <div style={{ position: "relative" }}>
                      <div
                        className={`${classes.ai} ${
                          aiDescriptionExpanded ? classes.expanded : ""
                        }`}
                      >
                        <Markdown>{art.aiMentorDescription}</Markdown>
                      </div>
                      <button
                        className={classes.toggle}
                        onClick={() => {
                          setAiDescriptionExpanded((prevState) => !prevState);
                        }}
                      >
                        {aiDescriptionExpanded ? t.collapse : t.expand}
                      </button>
                    </div>
                  )}
                </>
              )}
              {/* <div className={classes.rate}>
            <h2>Audience Award</h2>
            <div className={classes.points}>86%</div>
          </div> */}
              {/* <div className={classes.bar}>
            <div
              className={classes.barFilled}
              style={{ width: `${(86 / 100) * 100}%` }}
            ></div>
          </div> */}
              {/* <div className={classes.rate}>
                <h2>Category</h2>
              </div>
              <div className={classes.description}>{art.category}</div> */}
              <div className={classes.rate}>
                <h2>{t.tags}</h2>
                {/* <div className={classes.points}>{art.tags?.length} / 100</div> */}
              </div>
              <div className={classes.tags}>
                {art.tags?.map((tag, index) => (
                  <div key={`tag-${index}`} className={classes.tag}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 15"
                      fill="none"
                    >
                      <path
                        d="M3.14697 8.20187L5.80826 10.5882L12.8529 4.41177"
                        stroke="#868686"
                        strokeWidth="1.11111"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {tag}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {!screenIsLoading && art && (
          <div className={classes.battles}>
            <div className="container">
              <div className={classes.rate}>
                <h2>{t.battles}</h2>
                <div className={classes.points}>
                  <span
                    style={{ color: "rgba(0,0,0,0.5)", fontWeight: "normal" }}
                  >
                    {t.won}
                  </span>{" "}
                  {art.wonBattles} / {art.finishedBattles.length}
                </div>
              </div>
              {art.finishedBattles.length > 0 && (
                <div className={classes.bar}>
                  <div
                    className={classes.barFilled}
                    style={{
                      width: `${
                        (art.wonBattles / art.finishedBattles.length) * 100
                      }%`,
                    }}
                  >
                    {(
                      (art.wonBattles / art.finishedBattles.length) *
                      100
                    ).toFixed(0)}
                    %
                  </div>
                </div>
              )}
            </div>
            <Tabs
              variant="fullWidth"
              value={value}
              onChange={handleTabChange}
              className={classes.tabs}
            >
              <Tab
                value="pending"
                label={
                  <Badge
                    badgeContent={
                      art?.pendingBattles.length === 0
                        ? "0"
                        : art?.pendingBattles.length
                    }
                    color="primary"
                    className={classes.badge}
                  >
                    {t.pending}
                  </Badge>
                }
              />
              <Tab
                value="ongoing"
                label={
                  <Badge
                    badgeContent={
                      art?.ongoingBattles.length === 0
                        ? "0"
                        : art?.ongoingBattles.length
                    }
                    color="primary"
                    className={classes.badge}
                  >
                    {t.ongoing}
                  </Badge>
                }
              />
              <Tab
                value="finished"
                label={
                  <Badge
                    badgeContent={
                      art?.finishedBattles.length === 0
                        ? "0"
                        : art?.finishedBattles.length
                    }
                    color="primary"
                    className={classes.badge}
                  >
                    {t.finished}
                  </Badge>
                }
              />
            </Tabs>
            {value === "pending" && (
              <TabPanel value="pending">
                <div className="container">
                  {art && art.pendingBattles.length > 0 && (
                    <div className={classes.list}>
                      {art.pendingBattles.map((battle) => (
                        <PendingBattle
                          key={battle._id}
                          battleID={battle._id}
                          serverTime={art.serverTime}
                          artworkID={battle.artwork1ID}
                          artworkSrc={battle.artwork1Src}
                          createdAt={battle.createdAt}
                          level={battle.level}
                          nextLevelBattle={battle.nextLevelBattleForUser1}
                          topic={battle.topic}
                        />
                      ))}
                    </div>
                  )}
                  {art && art.pendingBattles.length === 0 && (
                    <div className={classes.empty}>{t.emptyPending}</div>
                  )}
                </div>
              </TabPanel>
            )}
            {value === "ongoing" && (
              <TabPanel value="ongoing">
                <div className="container">
                  {art && art.ongoingBattles.length > 0 && (
                    <div className={classes.list}>
                      {art.ongoingBattles.map((battle) => (
                        <OngoingBattle
                          setPopupImage={setPopupImage}
                          key={battle._id}
                          currentUserIndex={
                            battle.artwork1ID === art._id ? 0 : 1
                          }
                          {...battle}
                        />
                      ))}
                    </div>
                  )}
                  {art && art.ongoingBattles.length === 0 && (
                    <div className={classes.empty}>{t.emptyOngoing}</div>
                  )}
                </div>
              </TabPanel>
            )}
            {value === "finished" && (
              <TabPanel value="finished">
                <div className="container">
                  {art && art.finishedBattles.length > 0 && (
                    <div className={classes.list}>
                      {art.finishedBattles.map((battle) => (
                        <FinishedBattle
                          setPopupImage={setPopupImage}
                          key={battle._id}
                          currentUserIndex={
                            battle.artwork1ID === art._id ? 0 : 1
                          }
                          refreshUserBattles={getArtworkBattles}
                          {...battle}
                        />
                      ))}
                    </div>
                  )}
                  {art && art.finishedBattles.length === 0 && (
                    <div className={classes.empty}>{t.emptyFinished}</div>
                  )}
                </div>
              </TabPanel>
            )}
          </div>
        )}
      </section>
      <BottomSticky>
        <div className="container">
          <Button
            label={t.sendToBattle}
            onClick={() => {
              navigate(`/battle?slot=${slot}`);
            }}
          />
        </div>
      </BottomSticky>
    </>
  );
};

export default Image;
